import type { ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  IconArrowsExchange,
  IconLogout,
  IconSettings,
  IconShield,
  IconUser,
  type IconProps,
} from "@tabler/icons-react";
import moment from "moment/moment";
import { NavigationMenu } from "../NavigationMenu";
// @ts-expect-error pure js
import useLogout from "../../../hooks/use-logout.js";
// @ts-expect-error pure js
import useAccount from "../../../hooks/use-account.js";
// @ts-expect-error pure js
import useAuth from "../../../hooks/use-auth.js";
// @ts-expect-error pure js
import { TypeAbonnement } from "../../../utils/constants/account.js";
// @ts-expect-error pure js
import { classnames } from "../../../utils";
import DropdownMenu from "../DropdownMenu.tsx";
// @ts-expect-error pure js
import { Qalimo } from "../Icons.jsx";
import styles from "./PageLayout.module.css";
import Avatar from "../Avatar.tsx";
import BandeauInformation from "./BandeauInformation.tsx";
import useBandeauInformation from "../../../hooks/use-bandeau-information";
import { Button as BaseButton } from "@base-ui/react/button";

export interface LayoutProps {
  isTenant?: boolean;
  isBailleur?: boolean;
  isTBD?: boolean;
  children?: ReactNode;
}

interface UserMenuItem {
  label: string;
  icon: React.ComponentType<IconProps>;
  to: string;
}

export default function PageLayout({
  isTenant,
  isBailleur,
  isTBD,
  children,
}: LayoutProps) {
  const logout = useLogout();
  const navigate = useNavigate();
  const { auth, isImpersonated } = useAuth();
  const { account } = useAccount();
  const { visibleBandeau, dismiss } = useBandeauInformation(
    isTenant ? "locataire" : "bailleur",
  );

  if (isTBD) {
    return (
      <div className={styles.tbdContainer}>
        <button onClick={logout} className={styles.tbdLogout}>
          Déconnexion
        </button>
        {children}
      </div>
    );
  }

  const userMenuItems: UserMenuItem[] = [
    { label: "Mon compte", icon: IconSettings, to: "/mon-compte" },
  ];

  if (isBailleur) {
    userMenuItems.push({
      label: "Mode locataire",
      icon: IconArrowsExchange,
      to: "/locataire",
    });
  }

  if (isTenant) {
    userMenuItems.push({
      label: "Mode bailleur",
      icon: IconArrowsExchange,
      to: "/bailleur",
    });
  }

  if (auth.role?.length > 0) {
    userMenuItems.push({
      label: "Admin",
      icon: IconShield,
      to: "/admin",
    });
  }

  return (
    <>
      {isImpersonated && auth.mail !== auth.impersonator.mail && (
        <div className={classnames(styles.banner, styles.impersonated)}>
          Utilisateur usurpé : {auth.mail} ({auth.userId}) - Usurpateur :{" "}
          {auth.impersonator.mail} ({auth.impersonator.userId}){" "}
          <Link to={"/admin"}>Changer d'utilisateur</Link>
        </div>
      )}
      {account.ab_compte?.nom === "illimite" &&
        account.type_abonnement === TypeAbonnement.TRIAL &&
        moment(account.expiration_abonnement).isAfter(moment()) && (
          <div className={classnames(styles.banner, styles.trial)}>
            Vous êtes en période d'essai gratuit jusqu'au{" "}
            {moment(account.expiration_abonnement).format("DD/MM/YYYY")}.{" "}
            <Link to={"/mon-compte"}>S'abonner</Link>
          </div>
        )}
      <div className={styles.layout}>
        <header className={styles.header}>
          <div className={styles.brand}>
            <Qalimo />
            <span>alimo</span>
          </div>

          <DropdownMenu
            align="end"
            containerSelector="header > :last-child"
            nativeButton={false}
            trigger={
              <BaseButton
                nativeButton={false}
                className={styles.userMenuTrigger}
                render={
                  <div data-tour="header-user-menu">
                    <Avatar icon={<IconUser />} />
                  </div>
                }
              />
            }
          >
            {userMenuItems.map(({ to, label, icon }) => (
              <DropdownMenu.Item
                key={label}
                icon={icon}
                label={label}
                onClick={() => navigate(to)}
              />
            ))}
            <DropdownMenu.Item
              icon={IconLogout}
              label="Déconnexion"
              variant="danger"
              onClick={logout}
            />
          </DropdownMenu>
        </header>
        <div className={styles.sidebar}>
          <NavigationMenu isBailleur={!!isBailleur} isTenant={!!isTenant} />
        </div>
        <div className={classnames(styles.main)}>
          {visibleBandeau && (
            <BandeauInformation bandeau={visibleBandeau} onClose={dismiss} />
          )}
          {children}
        </div>
      </div>
    </>
  );
}
